/**
 * Native Platform Utilities for Capacitor
 * Provides platform detection and native feature initialization
 */

import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';
import { SplashScreen } from '@capacitor/splash-screen';
import { App } from '@capacitor/app';
import { logger } from './logger';

/**
 * Platform detection utilities
 */
export const platform = {
  isNative: Capacitor.isNativePlatform(),
  isIOS: Capacitor.getPlatform() === 'ios',
  isAndroid: Capacitor.getPlatform() === 'android',
  isWeb: Capacitor.getPlatform() === 'web',
  name: Capacitor.getPlatform()
};

/**
 * Configure status bar for native platforms
 */
const initStatusBar = async () => {
  if (!platform.isNative) return;

  try {
    // Set light content for dark backgrounds
    await StatusBar.setStyle({ style: Style.Light });

    if (platform.isAndroid) {
      // Set status bar color on Android
      await StatusBar.setBackgroundColor({ color: '#7C4DFF' });
    }

    logger.info('StatusBar initialized');
  } catch (error) {
    logger.error('Error initializing StatusBar', error);
  }
};

/**
 * Configure keyboard behavior
 */
const initKeyboard = async () => {
  if (!platform.isNative) return;

  try {
    // Listen for keyboard events
    Keyboard.addListener('keyboardWillShow', (info) => {
      document.body.classList.add('keyboard-visible');
      document.documentElement.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
    });

    Keyboard.addListener('keyboardWillHide', () => {
      document.body.classList.remove('keyboard-visible');
      document.documentElement.style.setProperty('--keyboard-height', '0px');
    });

    logger.info('Keyboard listeners initialized');
  } catch (error) {
    logger.error('Error initializing Keyboard', error);
  }
};

/**
 * Handle app state changes (background/foreground)
 */
const initAppState = async () => {
  if (!platform.isNative) return;

  try {
    App.addListener('appStateChange', ({ isActive }) => {
      logger.info('App state changed', { isActive });

      if (isActive) {
        // App came to foreground
        document.dispatchEvent(new CustomEvent('app:resume'));
      } else {
        // App went to background
        document.dispatchEvent(new CustomEvent('app:pause'));
      }
    });

    logger.info('App state listeners initialized');
  } catch (error) {
    logger.error('Error initializing app state', error);
  }
};

/**
 * Hide splash screen
 */
export const hideSplashScreen = async () => {
  if (!platform.isNative) return;

  try {
    await SplashScreen.hide();
    logger.info('Splash screen hidden');
  } catch (error) {
    logger.error('Error hiding splash screen', error);
  }
};

/**
 * Initialize all native features
 * Call this early in app initialization
 */
export const initNativeFeatures = async () => {
  if (!platform.isNative) {
    logger.info('Running on web platform, skipping native initialization');
    return;
  }

  logger.info('Initializing native features', { platform: platform.name });

  await Promise.all([
    initStatusBar(),
    initKeyboard(),
    initAppState()
  ]);

  logger.info('Native features initialized');
};

/**
 * Get safe area insets (for notches, home indicators)
 * Note: CSS env() variables are usually sufficient, but this provides JS access
 */
export const getSafeAreaInsets = () => {
  const computedStyle = getComputedStyle(document.documentElement);

  return {
    top: parseInt(computedStyle.getPropertyValue('--safe-area-inset-top') || '0', 10),
    right: parseInt(computedStyle.getPropertyValue('--safe-area-inset-right') || '0', 10),
    bottom: parseInt(computedStyle.getPropertyValue('--safe-area-inset-bottom') || '0', 10),
    left: parseInt(computedStyle.getPropertyValue('--safe-area-inset-left') || '0', 10)
  };
};
